<template>
  <b-modal v-model="selectColumnModalVisible" :title="editMode ? 'edit component' : 'add column to view'" @ok="add" size="lg" modal-class="view-editor-modal">
    <template #title>
      {{ editMode ? 'edit component' : 'add column to view' }}
      <modal-help topic="add-view-column" />
    </template>
    <b-container fluid>
      <b-form>
        <b-row>
          <b-form-group label="column type">
            <b-form-select v-model="selectedType" :options="columnTypeOptions" class="mb-3" size="sm" placeholder="selecttype" required />
          </b-form-group>
        </b-row>

        <!-- Regular column picker -->
        <b-row v-if="selectedType === 'column'">
          <b-form-group label="select column">
            <b-form-select v-model="selectedColumn" :options="spreadSheetColumnOptions" class="mb-3" size="sm" placeholder="selectcolumn" required />
          </b-form-group>
        </b-row>

        <!-- Custom text -->
        <b-row v-if="selectedType === 'custom'">
          <b-form-group label="custom text">
            <b-form-input v-model="customText" placeholder="custom text" required />
          </b-form-group>
        </b-row>

        <!-- Link Configuration -->
        <template v-if="selectedType === 'link'">
          <div class="link-config-panel">

            <!-- Link Target -->
            <div class="link-section">
              <div class="link-section-heading">Link Target</div>
              <div class="link-mode-options">
                <label class="link-mode-option" :class="{ active: linkMode === 'record' }" @click="linkMode = 'record'">
                  <input type="radio" v-model="linkMode" value="record" />
                  <div class="link-mode-content">
                    <span class="link-mode-title">This record's page</span>
                    <span class="link-mode-desc">opens a page in the context of the current row's record</span>
                  </div>
                </label>
                <label class="link-mode-option" :class="{ active: linkMode === 'relatedRecord', disabled: toOneRelationshipOptions.length === 0 }" @click="toOneRelationshipOptions.length > 0 && (linkMode = 'relatedRecord')">
                  <input type="radio" v-model="linkMode" value="relatedRecord" :disabled="toOneRelationshipOptions.length === 0" />
                  <div class="link-mode-content">
                    <span class="link-mode-title">Related record's page</span>
                    <span class="link-mode-desc" v-if="toOneRelationshipOptions.length > 0">opens a page for a record linked via a to-one relationship</span>
                    <span class="link-mode-desc link-mode-disabled-hint" v-else>no to-one relationships available on this spreadsheet</span>
                  </div>
                </label>
                <label class="link-mode-option" :class="{ active: linkMode === 'static' }" @click="linkMode = 'static'">
                  <input type="radio" v-model="linkMode" value="static" />
                  <div class="link-mode-content">
                    <span class="link-mode-title">Page (no record context)</span>
                    <span class="link-mode-desc">navigates to a page without passing any record</span>
                  </div>
                </label>
                <label class="link-mode-option" :class="{ active: linkMode === 'code' }" @click="linkMode = 'code'">
                  <input type="radio" v-model="linkMode" value="code" />
                  <div class="link-mode-content">
                    <span class="link-mode-title">Custom code</span>
                    <span class="link-mode-desc">runs JavaScript for this row instead of opening a page</span>
                  </div>
                </label>
              </div>
            </div>

            <!-- Relationship picker (relatedRecord mode) -->
            <div class="link-section" v-if="linkMode === 'relatedRecord'">
              <div class="link-section-heading">Relationship</div>
              <b-form-select v-model="relationshipColumn" :options="toOneRelationshipOptions" size="sm" class="mb-2" />
              <small class="link-hint" v-if="relationshipColumn && selectedRelationshipInfo">
                links to <strong>{{ selectedRelationshipInfo.relatedSpreadSheet }}</strong> via {{ selectedRelationshipInfo.title || relationshipColumn }}
              </small>
            </div>

            <div class="link-section" v-if="linkMode === 'code'">
              <div class="link-section-heading">JavaScript</div>
              <p class="link-hint">Runs when this link is clicked. The record is turbohammerRow. On a table, turbohammerRowElement is that table row. On a card, turbohammerCard is that card. turbohammerElement is the same element. It can call a function from the custom code files in app settings.</p>
              <details class="link-code-ref">
                <summary>Fields on this view</summary>
                <p v-if="codeReferenceOnTable" class="link-hint">Each line is one field on the clicked table row. The selector finds that cell inside turbohammerRowElement.</p>
                <p v-else-if="codeReferenceOnCard" class="link-hint">Each line is one field on the clicked card. The selector finds that field inside turbohammerCard.</p>
                <p v-else class="link-hint">Each line is one field on this view. The record property is on turbohammerRow. On a table or a card, the selector finds that field inside turbohammerElement.</p>
                <code class="link-code-ref-example">{{ codeFieldExample }}</code>
                <ul v-if="codeFieldGuide.length" class="link-code-ref-list">
                  <li v-for="field in codeFieldGuide" :key="field.uniqueTitle">
                    <span class="link-code-ref-label">{{ field.label }}</span>
                    <code>{{ field.query }}</code>
                    <span class="link-code-ref-record">{{ field.record }}</span>
                  </li>
                </ul>
                <p v-else class="link-hint">Columns already on this view are listed here.</p>
                <p class="link-hint">On a table the match is the cell. On a card it is the field, and .card-field-value inside it is the text. A link in the field is .table-link or .card-link. A button is .view-link-button. An editable table cell is input.editable-cell-input.</p>
              </details>
              <div class="link-code-box">
                <JsCodeEditor v-model="linkCode" aria-label="JavaScript for this link" />
              </div>
            </div>

            <!-- Page selection -->
            <div class="link-section" v-if="linkMode !== 'code'">
              <div class="link-section-heading">Page</div>
              <div class="page-source-toggle">
                <label class="page-source-option" :class="{ active: pageSource === 'new' }">
                  <input type="radio" v-model="pageSource" value="new" />
                  create new page
                </label>
                <label class="page-source-option" :class="{ active: pageSource === 'existing' }">
                  <input type="radio" v-model="pageSource" value="existing" />
                  use existing page
                </label>
              </div>

              <!-- New page fields -->
              <template v-if="pageSource === 'new'">
                <b-form-group label="page title" class="mt-2">
                  <b-form-input v-model="newPageTitle" placeholder="My Page" size="sm" required />
                </b-form-group>
                <b-form-group label="page unique title">
                  <b-form-input v-model="newPageUniqueTitle" placeholder="my_page" size="sm" required />
                </b-form-group>
              </template>

              <!-- Existing page picker -->
              <b-form-group v-if="pageSource === 'existing'" label="select page" class="mt-2">
                <b-form-select v-model="existingPageUniqueTitle" :options="pageOptions" size="sm" />
              </b-form-group>

              <!-- Page context summary -->
              <div class="page-context-summary" v-if="resolvedPageUniqueTitle">
                <span class="page-context-icon">&#128279;</span>
                <span class="page-context-text">
                  <template v-if="linkMode === 'record'">
                    each row links to <strong>{{ resolvedPageUniqueTitle }}</strong> for that row's record
                  </template>
                  <template v-else-if="linkMode === 'relatedRecord' && relationshipColumn">
                    each row links to <strong>{{ resolvedPageUniqueTitle }}</strong> for the related <strong>{{ selectedRelationshipInfo ? selectedRelationshipInfo.relatedSpreadSheet : relationshipColumn }}</strong> record
                  </template>
                  <template v-else-if="linkMode === 'static'">
                    all rows link to <strong>{{ resolvedPageUniqueTitle }}</strong> (same destination)
                  </template>
                </span>
              </div>
            </div>

            <!-- Link text -->
            <div class="link-section">
              <div class="link-section-heading">Link Text</div>
              <b-form-group label="display as">
                <b-form-select v-model="linkTextType" :options="linkTextTypeOptions" size="sm" class="mb-2" />
              </b-form-group>
              <b-form-group v-if="linkTextType === 'column'" label="column to display">
                <b-form-select v-model="selectedColumn" :options="directColumnOptions" size="sm" />
              </b-form-group>
              <b-form-group v-if="linkTextType === 'custom'" label="custom text">
                <b-form-input v-model="customText" placeholder="View details" size="sm" />
              </b-form-group>
            </div>

            <div class="link-section">
              <div class="link-section-heading">Appearance</div>
              <label class="editable-toggle">
                <input type="checkbox" v-model="displayAsButton" />
                show as a button
              </label>
              <small class="link-hint">Visitors see a button on the table, card, map, and calendar.</small>
            </div>

          </div>
        </template>

        <!-- Label -->
        <b-row>
          <b-form-group label="label">
            <small class="field-hint">shown above the value. leave blank to hide it</small>
            <b-form-input v-model="label" placeholder="column label" />
          </b-form-group>
        </b-row>

        <b-row v-if="selectedType === 'column' && isMultiselectColumn">
          <b-form-group label="choice display">
            <small class="field-hint">how the saved choices appear in this view</small>
            <b-form-select v-model="multiselectDisplay" :options="multiselectDisplayOptions" size="sm" />
          </b-form-group>
        </b-row>

        <value-formatting-fields
          v-if="selectedType === 'column' && formatKind"
          ref="formattingFields"
          :format-kind="formatKind"
          :seed="formattingSeed"
          :inherited="inheritedFormatting"
          :date-text="isDateText"
          @formatting-change="formattingDraft = $event"
        />

        <template v-if="isTableContext && selectedType === 'action'">
          <b-row>
            <b-form-group label="action name">
              <b-form-input v-model="actionId" placeholder="mark_paid" />
            </b-form-group>
          </b-row>
          <b-row>
            <b-form-group label="what it does">
              <b-form-select v-model="actionKind" :options="actionKindOptions" size="sm" />
            </b-form-group>
          </b-row>
          <b-row v-if="actionKind === 'update' || actionKind === 'related'">
            <b-form-group label="column">
              <b-form-input v-model="actionColumn" placeholder="status" size="sm" />
            </b-form-group>
            <b-form-group label="value">
              <b-form-input v-model="actionValue" placeholder="paid" size="sm" />
            </b-form-group>
          </b-row>
          <b-row v-if="actionKind === 'related'">
            <b-form-group label="relationship column">
              <b-form-input v-model="actionRelationship" placeholder="orders" size="sm" />
            </b-form-group>
            <b-form-group label="related rows">
              <b-form-select v-model="actionRelatedMode" :options="[{ value: 'update', text: 'update linked rows' }, { value: 'insert', text: 'insert a linked row' }]" size="sm" />
            </b-form-group>
          </b-row>
          <b-row v-if="actionKind === 'email'">
            <b-form-group label="email automation">
              <b-form-input v-model="actionAutomation" placeholder="mail_paid" size="sm" />
            </b-form-group>
          </b-row>
          <b-row v-if="actionKind === 'workflow'">
            <b-form-group label="workflow">
              <b-form-input v-model="actionWorkflow" placeholder="after_paid" size="sm" />
            </b-form-group>
          </b-row>
          <p v-if="actionError" class="text-danger">{{ actionError }}</p>
        </template>

        <template v-if="isTableContext && selectedType === 'column'">
          <b-row>
            <b-form-group label="column width">
              <b-form-input v-model="columnWidth" type="number" min="1" placeholder="auto" size="sm" />
            </b-form-group>
            <b-form-group label="empty text">
              <b-form-input v-model="emptyText" placeholder="shown when the cell is blank" size="sm" />
            </b-form-group>
          </b-row>
          <b-row>
            <b-form-group label="footer">
              <b-form-select v-model="summary" :options="summaryOptions" size="sm" />
            </b-form-group>
          </b-row>
          <b-row>
            <label class="editable-toggle">
              <input type="checkbox" v-model="hidden" />
              hide this column from the table and from export
            </label>
          </b-row>
        </template>

        <b-row v-if="isTableContext" class="field-group">
          <b-form-group>
            <label class="editable-toggle">
              <input type="checkbox" v-model="disableSorting" />
              disable sorting. users cannot reorder records by this column.
            </label>
          </b-form-group>
        </b-row>

        <!-- Editable fields (table context only, not for links) -->
        <template v-if="isTableContext && selectedType === 'column' && !selectedColumnIsRelated">
          <b-row class="field-group">
            <b-form-group>
              <label class="editable-toggle">
                <input type="checkbox" v-model="editable" />
                editable — allow users to edit this field inline
              </label>
            </b-form-group>
          </b-row>
          <template v-if="editable">
            <b-row class="field-group">
              <b-form-group label="placeholder text">
                <small class="field-hint">displayed inside the input when empty</small>
                <b-form-input v-model="placeholder" size="sm" />
              </b-form-group>
            </b-row>
            <b-row class="field-group">
              <b-form-group label="description">
                <small class="field-hint">help text displayed below the input</small>
                <b-form-input v-model="description" size="sm" />
              </b-form-group>
            </b-row>
            <b-row class="field-group">
              <b-form-group label="rules">
                <small class="field-hint">configure automation rules for this editable field</small>
                <div class="rules-button-group">
                  <button
                    type="button"
                    @click="defaultValueRuleEditorVisible = true"
                    class="field-rules-btn"
                    :class="{ 'has-rules': defaultValueRules && defaultValueRules.length }"
                  >
                    &#9889; Default value rules
                    <span v-if="defaultValueRules && defaultValueRules.length" class="field-rules-count">{{ defaultValueRules.length }}</span>
                  </button>
                  <button
                    type="button"
                    @click="submissionRuleEditorVisible = true"
                    class="field-rules-btn"
                    :class="{ 'has-rules': submissionRules && submissionRules.length }"
                  >
                    &#9889; Submission rules
                    <span v-if="submissionRules && submissionRules.length" class="field-rules-count">{{ submissionRules.length }}</span>
                  </button>
                </div>
              </b-form-group>
            </b-row>
          </template>
        </template>
      </b-form>
    </b-container>
  </b-modal>
  <DefaultValueRuleEditor
    v-if="isTableContext"
    :visible="defaultValueRuleEditorVisible"
    :rules="defaultValueRules"
    :spreadsheet="parentEditor ? parentEditor.spreadsheet : null"
    :all-spreadsheets="parentEditor ? parentEditor.allSpreadsheets : []"
    :application-unique-title="parentEditor ? parentEditor.applicationUniqueTitle : ''"
    :pages="parentEditor && parentEditor.pages ? parentEditor.pages : []"
    :current-page-unique-title="parentEditor && parentEditor.currentPageUniqueTitle ? parentEditor.currentPageUniqueTitle : ''"
    @update:visible="defaultValueRuleEditorVisible = $event"
    @update:rules="defaultValueRules = $event"
  />
  <SubmissionRuleEditor
    v-if="isTableContext"
    :visible="submissionRuleEditorVisible"
    :rules="submissionRules"
    :spreadsheet="parentEditor ? parentEditor.spreadsheet : null"
    :all-spreadsheets="parentEditor ? parentEditor.allSpreadsheets : []"
    :pages="parentEditor && parentEditor.pages ? parentEditor.pages : []"
    :page-components="parentEditor && parentEditor.pageComponents ? parentEditor.pageComponents : []"
    :current-page-unique-title="parentEditor && parentEditor.currentPageUniqueTitle ? parentEditor.currentPageUniqueTitle : ''"
    :application-unique-title="parentEditor ? parentEditor.applicationUniqueTitle : ''"
    @update:visible="submissionRuleEditorVisible = $event"
    @update:rules="submissionRules = $event"
  />
</template>

<script>

  import axios from 'axios'
  import SubmissionRuleEditor from './lib/SubmissionRuleEditor.vue'
  import DefaultValueRuleEditor from './lib/DefaultValueRuleEditor.vue'
  import ValueFormattingFields from './lib/ValueFormattingFields.vue'
  import { isTemplateFormula } from '../lib/formula-utils.js'
  import { valueFormatKind } from '../../../shared/value-format.js'
  import { viewCodeFieldGuide } from '../../../shared/view-fields.js'
  import JsCodeEditor from './JsCodeEditor.vue'

  function newCodeLinkTitle () {

    return `code_${Math.random().toString(36).slice(2, 8)}`
  }

  export default {

    components: {
      SubmissionRuleEditor,
      DefaultValueRuleEditor,
      ValueFormattingFields,
      JsCodeEditor
    },

    data() {

      return {
        selectColumnModalVisible: false,
        selectedColumn: {},
        spreadSheetColumnOptions: [],
        typeOptions: [
          { value: 'column', text: 'column' },
          { value: 'link', text: 'link' },
          { value: 'custom', text: 'custom text' }
        ],
        selectedType: {},
        label: ``,
        customText: ``,
        parentEditor: null,
        editMode: false,
        editable: false,
        disableSorting: false,
        placeholder: ``,
        description: ``,
        submissionRules: [],
        defaultValueRules: [],
        columnWidth: '',
        emptyText: '',
        summary: '',
        hidden: false,
        multiselectDisplay: 'comma',
        formattingSeed: null,
        formattingDraft: null,
        multiselectDisplayOptions: [
          { value: 'comma', text: 'comma separated' },
          { value: 'newline', text: 'new line' },
          { value: 'bullets', text: 'bullet list' }
        ],
        actionId: '',
        actionKind: 'update',
        actionColumn: '',
        actionValue: '',
        actionRelationship: '',
        actionRelatedMode: 'update',
        actionAutomation: '',
        actionWorkflow: '',
        actionError: '',
        summaryOptions: [
          { value: '', text: 'none' },
          { value: 'sum', text: 'sum' },
          { value: 'average', text: 'average' },
          { value: 'min', text: 'min' },
          { value: 'max', text: 'max' }
        ],
        actionKindOptions: [
          { value: 'update', text: 'update this row' },
          { value: 'related', text: 'update or insert related rows' },
          { value: 'email', text: 'send an email automation' },
          { value: 'workflow', text: 'start a workflow' }
        ],
        submissionRuleEditorVisible: false,
        defaultValueRuleEditorVisible: false,

        // Link configuration
        linkMode: 'record',
        displayAsButton: false,
        linkCode: '',
        codeLinkTitle: '',
        relationshipColumn: '',
        pageSource: 'existing',
        newPageTitle: '',
        newPageUniqueTitle: '',
        existingPageUniqueTitle: '',
        linkTextType: 'column',
        autoPopulatedLabel: ``,
        suppressLabelFill: false,
        pageOptions: [],
        linkTextTypeOptions: [
          { value: 'column', text: 'column value' },
          { value: 'custom', text: 'custom text' }
        ]
      }
    },
    computed: {
      columnTypeOptions () {

        if (!this.isTableContext) return this.typeOptions

        return this.typeOptions.concat([{ value: 'action', text: 'action' }])
      },
      isTableContext () {
        return !!(this.parentEditor && this.parentEditor.table)
      },
      toOneRelationshipOptions () {
        if (!this.parentEditor || !this.parentEditor.spreadsheet || !this.parentEditor.spreadsheet.columns) {
          return []
        }
        return this.parentEditor.spreadsheet.columns
          .filter(col => col.type === 'relationship' && (col.relationshipType === 'oneToOne' || col.relationshipType === 'manyToOne'))
          .map(col => ({
            value: col.uniqueTitle,
            text: `${col.title || col.uniqueTitle} → ${col.relatedSpreadSheet}`
          }))
      },
      selectedRelationshipInfo () {
        if (!this.relationshipColumn || !this.parentEditor || !this.parentEditor.spreadsheet || !this.parentEditor.spreadsheet.columns) {
          return null
        }
        return this.parentEditor.spreadsheet.columns.find(col => col.uniqueTitle === this.relationshipColumn)
      },
      resolvedPageUniqueTitle () {
        if (this.pageSource === 'new') {
          return this.newPageUniqueTitle || null
        }
        return this.existingPageUniqueTitle || null
      },
      selectedColumnOption () {
        return (this.spreadSheetColumnOptions || []).find(option => option.value === this.selectedColumn) || null
      },
      selectedColumnIsRelated () {
        return !!(this.selectedColumnOption && this.selectedColumnOption.relatedSource)
      },
      sourceColumn () {

        if (this.selectedType !== 'column') return null

        const option = this.selectedColumnOption

        if (!option) return null

        if (option.relatedSource) {

          return {
            type: option.relatedFieldType || 'text',
            formula: option.formula || '',
            valueFormatting: option.valueFormatting || null
          }
        }

        const columns = this.parentEditor && this.parentEditor.spreadsheet && this.parentEditor.spreadsheet.columns
        const column = (columns || []).find(item => item.uniqueTitle === option.value)

        if (column) return column

        const systemTypes = { id: 'number', createdAt: 'dateTime', updatedAt: 'dateTime' }

        if (systemTypes[option.value]) return { type: systemTypes[option.value], uniqueTitle: option.value }

        return null
      },
      formatKind () {

        const column = this.sourceColumn

        if (!column) return null
        if (column.type === 'formula') return isTemplateFormula(column.formula) ? 'text' : 'number'

        return valueFormatKind(column)
      },
      isDateText () {

        const column = this.sourceColumn

        return !!(column && (column.type === 'dateTime' || column.uniqueTitle === 'createdAt' || column.uniqueTitle === 'updatedAt'))
      },
      inheritedFormatting () {

        const formatting = this.sourceColumn && this.sourceColumn.valueFormatting

        if (!formatting || formatting.type !== this.formatKind) return null

        return formatting
      },
      isMultiselectColumn () {
        if (this.selectedType !== 'column') return false
        const option = this.selectedColumnOption
        if (!option) return false
        if (option.relatedFieldType) return option.relatedFieldType === 'multiselect'
        const columns = this.parentEditor && this.parentEditor.spreadsheet && this.parentEditor.spreadsheet.columns
        const column = (columns || []).find(item => item.uniqueTitle === option.value)
        return !!(column && column.type === 'multiselect')
      },
      directColumnOptions () {
        return (this.spreadSheetColumnOptions || []).filter(option => !option.relatedSource)
      },
      codeReferenceOnTable () {

        return !!(this.parentEditor && this.parentEditor.table)
      },
      codeReferenceOnCard () {

        const editor = this.parentEditor

        return !!(editor && editor.cards && editor.isCardsLayout)
      },
      codeFieldGuide () {

        const editor = this.parentEditor

        if (!editor) return []

        const components = editor.table
          ? editor.table.components
          : (editor.cards ? editor.cards.components : [])

        return viewCodeFieldGuide(components)
      },
      codeFieldExample () {

        const named = this.codeFieldGuide.find((field) => field.uniqueTitle === 'name')
        const field = named || this.codeFieldGuide[0]

        if (field) return field.query

        return 'turbohammerElement.querySelector(\'[data-field="name"]\')'
      }
    },
    watch: {
      selectedColumn (value) {
        const selected = (this.spreadSheetColumnOptions || []).find(option => option.value === value)
        if (selected && selected.relatedSource) this.editable = false
        if (this.suppressLabelFill) return
        if (selected && (this.label === `` || this.label === this.autoPopulatedLabel)) {
          this.label = selected.text
          this.autoPopulatedLabel = selected.text
        }
      },
      selectedType (val) {
        // Reset link config when switching away from link type
        if (val !== 'link') {
          this.linkMode = 'record'
          this.relationshipColumn = ''
          this.pageSource = 'existing'
        }
      }
    },
    methods: {
      displayConfig () {

        return {
          columnWidth: this.columnWidth,
          emptyText: this.emptyText,
          summary: this.summary,
          hidden: this.hidden,
          actionId: this.actionId,
          actionKind: this.actionKind,
          actionValues: this.actionColumn ? [{ column: this.actionColumn, value: this.actionValue }] : [],
          actionRelationship: this.actionRelationship,
          actionRelatedMode: this.actionRelatedMode,
          actionAutomation: this.actionAutomation,
          actionWorkflow: this.actionWorkflow,
          submissionRules: { rules: this.submissionRules },
          multiselectDisplay: this.isMultiselectColumn ? (this.multiselectDisplay || 'comma') : '',
          valueFormatting: this.currentFormatting()
        }
      },
      currentFormatting () {

        if (this.selectedType !== 'column' || !this.formatKind) return null

        const fields = this.$refs.formattingFields

        if (fields && typeof fields.output === 'function') return fields.output()

        return this.formattingDraft
      },
      async add (event) {

        if (this.selectedType === 'action' && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(this.actionId || '')) {

          if (event && event.preventDefault) event.preventDefault()
          this.actionError = 'Use a name that starts with a letter or underscore.'

          return
        }

        this.actionError = ''

        let type = this.selectedType
        let childPageUniqueTitle = null

        if (type === 'link' && this.linkMode !== 'code') {

          // Resolve the page unique title
          childPageUniqueTitle = this.resolvedPageUniqueTitle

          // If creating a new page, save it
          if (this.pageSource === 'new') {
            await this.saveNewPage()
          }

          // Resolve link text
          if (this.linkTextType === 'custom') {
            this.selectedColumn = ''
          }
        }

        if (type === 'link' && this.linkMode === 'code' && this.linkTextType === 'custom') {

          this.selectedColumn = this.codeLinkTitle || newCodeLinkTitle()
        }

        if (this.parentEditor.cards) {
          this.parentEditor.showEditCardsComponent(this.parentEditor.cards.components, this.parentEditor.spreadSheetUniqueTitle)
        } else if (this.parentEditor.table) {
          this.parentEditor.showEditTableComponent(this.parentEditor.table.components, this.parentEditor.spreadSheetUniqueTitle)
        }

        const editableConfig = (this.isTableContext && type === 'column') ? {
          editable: this.editable,
          placeholder: this.placeholder,
          description: this.description,
          submissionRules: { rules: this.submissionRules },
          defaultValueRules: { rules: this.defaultValueRules }
        } : null

        const linkConfig = type === 'link' ? {
          linkMode: this.linkMode,
          relationshipColumn: this.linkMode === 'relatedRecord' ? this.relationshipColumn : null,
          code: this.linkMode === 'code' ? this.linkCode : '',
          displayAsButton: !!this.displayAsButton
        } : null

        const display = this.displayConfig()
        const label = String(this.label == null ? '' : this.label).trim()

        if (this.editMode) {
          this.parentEditor.editComponentInActiveSlot(this.selectedColumn, label, type, childPageUniqueTitle, this.customText, editableConfig, linkConfig, this.isTableContext && this.disableSorting, display)
        } else {
          this.parentEditor.addComponentToActiveSlot(this.selectedColumn, label, type, childPageUniqueTitle, this.customText, editableConfig, linkConfig, this.isTableContext && this.disableSorting, display)
        }
      },
      showSelectColumnModal () {

        this.editMode = false
        this.suppressLabelFill = false
        this.selectedType = {}
        this.selectedColumn = {}
        this.label = ``
        this.autoPopulatedLabel = ``
        this.customText = ``
        this.editable = false
        this.disableSorting = false
        this.placeholder = ``
        this.description = ``
        this.submissionRules = []
        this.defaultValueRules = []
        this.columnWidth = ''
        this.emptyText = ''
        this.summary = ''
        this.hidden = false
        this.multiselectDisplay = 'comma'
        this.formattingSeed = null
        this.formattingDraft = null
        this.actionId = ''
        this.actionKind = 'update'
        this.actionColumn = ''
        this.actionValue = ''
        this.actionRelationship = ''
        this.actionRelatedMode = 'update'
        this.actionAutomation = ''
        this.actionWorkflow = ''
        this.actionError = ''
        this.linkMode = 'record'
        this.displayAsButton = false
        this.linkCode = ''
        this.codeLinkTitle = ''
        this.relationshipColumn = ''
        this.pageSource = 'existing'
        this.newPageTitle = ''
        this.newPageUniqueTitle = ''
        this.existingPageUniqueTitle = ''
        this.linkTextType = 'column'
        this.selectColumnModalVisible = true
        this.loadPages()
      },
      showEditColumnModal (component) {

        this.editMode = true
        this.suppressLabelFill = true
        this.autoPopulatedLabel = ``
        this.displayAsButton = false
        this.label = typeof component.label === 'string' ? component.label : ''

        if (component.type === 'link') {
          this.selectedType = 'link'
          this.linkMode = component.linkMode || 'record'
          this.displayAsButton = !!component.displayAsButton
          this.linkCode = component.code || ''
          this.relationshipColumn = component.relationshipColumn || ''
          this.pageSource = 'existing'
          this.existingPageUniqueTitle = component.childPageUniqueTitle || ''
          this.codeLinkTitle = /^code_[a-z0-9]+$/.test(component.uniqueTitle || '') ? component.uniqueTitle : ''

          // Determine link text type
          if (this.codeLinkTitle || (component.customText && !component.uniqueTitle)) {
            this.linkTextType = 'custom'
            this.customText = component.customText || ''
            this.selectedColumn = ''
          } else {
            this.linkTextType = 'column'
            this.selectedColumn = component.uniqueTitle || ''
            this.customText = component.customText || ''
          }
        } else if (component.type === 'action') {
          this.selectedType = 'action'
          this.actionId = component.uniqueTitle || ''
          this.actionKind = component.actionKind || 'update'
          const first = (component.actionValues || [])[0] || {}
          this.actionColumn = first.column || ''
          this.actionValue = first.value || ''
          this.actionRelationship = component.actionRelationship || ''
          this.actionRelatedMode = component.actionRelatedMode || 'update'
          this.actionAutomation = component.actionAutomation || ''
          this.actionWorkflow = component.actionWorkflow || ''
        } else if (component.customText && !component.uniqueTitle) {
          this.selectedType = 'custom'
          this.customText = component.customText || ''
        } else {
          this.selectedType = 'column'
          this.selectedColumn = component.uniqueTitle
        }

        this.columnWidth = component.columnWidth || ''
        this.emptyText = component.emptyText || ''
        this.summary = component.summary || ''
        this.hidden = !!component.hidden
        this.multiselectDisplay = component.multiselectDisplay === 'newline' || component.multiselectDisplay === 'bullets'
          ? component.multiselectDisplay
          : 'comma'
        this.formattingSeed = component.valueFormatting && component.valueFormatting.type
          ? JSON.parse(JSON.stringify(component.valueFormatting))
          : null
        this.formattingDraft = this.formattingSeed
        this.editable = !!component.editable
        this.disableSorting = !!component.disableSorting
        this.placeholder = component.placeholder || ''
        this.description = component.description || ''
        this.submissionRules = (component.submissionRules && component.submissionRules.rules) ? component.submissionRules.rules : []
        this.defaultValueRules = (component.defaultValueRules && component.defaultValueRules.rules) ? component.defaultValueRules.rules : []
        this.selectColumnModalVisible = true
        this.loadPages()
        this.$nextTick(() => { this.suppressLabelFill = false })
      },
      async loadPages () {
        try {
          const applicationUniqueTitle = this.parentEditor ? (this.parentEditor.applicationUniqueTitle || '') : ''
          const pages = (await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/pages/`, {
            headers: { 'application-unique-title': applicationUniqueTitle }
          })).data
          this.pageOptions = pages.map(page => ({
            value: page.uniqueTitle,
            text: page.title || page.uniqueTitle
          }))
        } catch (error) {
          console.log('error loading pages:', error)
        }
      },
      async saveNewPage () {

        const body = {
          title: this.newPageTitle,
          uniqueTitle: this.newPageUniqueTitle,
          parentUniqueTitle: this.parentEditor.currentPageUniqueTitle || null
        }

        const applicationUniqueTitle = this.parentEditor.applicationUniqueTitle || ''

        console.log('saving new page with body:', body)

        try {
          await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/pages/`, body, {
            headers: { 'application-unique-title': applicationUniqueTitle }
          })
        } catch (error) {
          console.log('error creating page:', error)
        }

        await window.design.getPages()
      }
    }
  }
</script>

<style scoped>
/* ── Link Configuration Panel ── */
.link-config-panel {
  border: 1px solid #2a2a28;
  border-radius: 6px;
  padding: 16px;
  margin: 8px 0 16px;
  background: #161614;
}

.link-section {
  margin-bottom: 18px;
}

.link-section:last-child {
  margin-bottom: 0;
}

.link-section-heading {
  color: #9ad12b;
  font-size: 0.72em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 10px;
  padding-bottom: 4px;
  border-bottom: 1px solid #2a2a28;
}

/* ── Link Mode Radio Options ── */
.link-mode-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.link-mode-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #2a2a28;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.15s;
  background: #1a1a18;
  margin: 0;
}

.link-mode-option:hover {
  border-color: #3a3a38;
  background: #1e1e1b;
}

.link-mode-option.active {
  border-color: #9ad12b;
  background: #1a2510;
}

.link-mode-option.disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.link-mode-option input[type="radio"] {
  margin-top: 2px;
  accent-color: #9ad12b;
  flex-shrink: 0;
}

.link-mode-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.link-mode-title {
  color: #d0d0ce;
  font-size: 0.88em;
  font-weight: 500;
}

.link-mode-desc {
  color: #6a6a67;
  font-size: 0.78em;
  line-height: 1.4;
}

.link-mode-disabled-hint {
  color: #555550;
  font-style: italic;
}

/* ── Page Source Toggle ── */
.page-source-toggle {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.page-source-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #2a2a28;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.82em;
  color: #8a8a88;
  transition: all 0.15s;
  background: #1a1a18;
  margin: 0;
}

.page-source-option:hover {
  border-color: #3a3a38;
}

.page-source-option.active {
  border-color: #9ad12b;
  color: #d0d0ce;
  background: #1a2510;
}

.page-source-option input[type="radio"] {
  accent-color: #9ad12b;
}

/* ── Page Context Summary ── */
.page-context-summary {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: #1a2510;
  border: 1px solid rgba(154, 209, 43, 0.2);
  border-radius: 5px;
  margin-top: 10px;
}

.page-context-icon {
  font-size: 1em;
  flex-shrink: 0;
}

.page-context-text {
  font-size: 0.82em;
  color: #b0b0ae;
  line-height: 1.5;
}

.page-context-text strong {
  color: #9ad12b;
}

/* ── Hints ── */
.link-hint {
  color: #6a6a67;
  font-size: 0.8em;
  display: block;
  line-height: 1.4;
}

.link-hint strong {
  color: #9ad12b;
}

.link-code-ref {
  margin: 0.55rem 0 0.35rem;
  border: 1px solid #2a2a27;
  border-radius: 8px;
  background: #141412;
}

.link-code-ref-example {
  display: block;
  margin: 0 0.7rem 0.55rem;
  padding: 0.4rem 0.5rem;
  border-radius: 6px;
  background: #10100e;
  border: 1px solid #2a2a27;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.72rem;
  color: #c6ee7a;
  overflow-wrap: anywhere;
}

.link-code-ref summary {
  cursor: pointer;
  padding: 0.45rem 0.7rem;
  color: #c6ee7a;
  font-size: 0.75rem;
  font-weight: 600;
}

.link-code-ref .link-hint {
  padding: 0 0.7rem;
}

.link-code-ref .link-hint:last-child {
  padding-bottom: 0.65rem;
}

.link-code-ref-list {
  list-style: none;
  margin: 0.15rem 0 0.45rem;
  padding: 0 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.link-code-ref-list li {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.link-code-ref-label {
  color: #e8e8e6;
  font-size: 0.75rem;
}

.link-code-ref code,
.link-code-ref-record {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.72rem;
  color: #c6ee7a;
  overflow-wrap: anywhere;
}

.link-code-ref-record {
  color: #8a8a86;
}

.link-code-box {
  height: 8.5rem;
  margin-top: 0.4rem;
}

.link-code-box :deep(.js-code) {
  height: 100%;
}

/* ── Existing styles ── */
.field-hint {
  font-style: italic;
  color: #888;
  display: block;
  margin-bottom: 4px;
}
.field-group {
  margin-top: 12px;
}
.editable-toggle {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  font-size: 0.9em;
  color: #ccc;
  line-height: 1.5;
}
.editable-toggle input[type="checkbox"] {
  margin-top: 4px;
  accent-color: #9ad12b;
}
.rules-button-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 6px;
}
.field-rules-btn {
  color: #7a7a78;
  background: #1e1e1b;
  border: 1px dashed #3a3a38;
  padding: 8px 14px;
  border-radius: 5px;
  font-size: 0.82em;
  cursor: pointer;
  transition: all 0.15s;
}
.field-rules-btn:hover {
  color: #9ad12b;
  background: #1e2e12;
  border-color: #9ad12b;
}
.field-rules-btn.has-rules {
  color: #9ad12b;
  border-color: #9ad12b;
  border-style: solid;
  background: #1e2e12;
}
.field-rules-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  font-size: 0.75em;
  font-weight: 700;
  background: #9ad12b;
  color: #141412;
  margin-left: 5px;
  padding: 0 4px;
}
</style>
